/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import { func, object, string } from 'prop-types';
import { makeStyles } from '@material-ui/core';
import mbpLogger from '@module-logger/mbp';
import ReactHtmlParser from 'react-html-parser';
import ReactMarkdown from 'react-markdown';
import ChatSelectDialog from './ChatSelectDialog';
import formatTimeAgo from '../utils/timeAgo';
import ResponsiveImage from '../../GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { supportListpicker } from './FirstListPickerConstants';
import imageMapper from './listPickerImageConstants';
import {
    isInteractive,
    isReadyMessage,
    isSelfMessage,
    isServerMessage,
    isSurveyLink,
    urlConverter,
    getHostname,
} from '../utils/AigoWidgetHelper';
import ExperienceRating from './ChatSurvey';

const useStyles = makeStyles((theme) => ({
    welcome: {
        display: 'flex',
        justifyContent: 'center',
    },
    wMessage: {
        width: '100%',
        margin: '0 0px',
        padding: '15px',
        paddingTop: '5px',
    },
    wMessageTitle: {
        display: 'flex',
        alignItems: 'center',
    },
    logo: {
        width: '26px',
        marginBottom: '5px',
        marginRight: '5px',
        height: '35px',
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: 600,
        marginBottom: '5px',
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '10px',
        '&:a': {
            border: `1px solid  ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
            color: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
            fontWeight: 'bold',
            borderRadius: '15px',
            width: '80px',
        },
    },
    confirmBtn: {
        marginLeft: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        '&:hover': {
            color: '#fff',
        },
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        border: `2px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
    },
    confirmBtnOutline: {
        backgroundColor: '#fff',
        color: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        border: `2px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
    },
    bubble: {
        display: 'inline-block',
        wordBreak: 'break-word',
    },
    selectedBubble: {
        padding: '15px 0',
        color: '#999',
        display: 'flex',
        alignItems: 'center',
        maxWidth: 370,
    },
    selOptLink: {
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        textDecoration: 'none',
        textAlign: 'left',
        padding: '0px 10px',
        '&:hover': {
            color: '#333',
        },
        '&:focus': {
            outline: 'none',
        },
        '& img': {
            marginLeft: '1.2rem',
            borderRadius: '8px',
        },
    },
    selOptLinkSpan: {
        width: '80%',
        display: 'block',
        justifyItems: 'start',
        marginRight: '5px',
    },
    selOptLinkTitle: {
        fontWeight: 600,
        fontSize: '16px',
        display: 'table-row',
    },
    selOptLinkSubtitle: {
        lineHeight: '1rem',
        color: '#999',
        fontSize: '0.75rem',
        textAlign: 'left',
        display: 'table-row',
    },
    selOptLinkImg: {
        display: 'table-cell',
    },
    surveyLink: {
        textDecoration: 'none',
        padding: '0px',
    },
    bubbleMessageImg: {
        borderRadius: '8px',
    },
    mx2: {
        width: '55px',
        height: '55px',
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
    },
    listItemName: {
        flex: 1,
        display: 'inline-block',
        flexDirection: 'column',
        verticalAlign: 'middle',
        color: '#FFF',
    },
    itemImg: {
        width: '45px',
        height: '45px',
        borderRadius: '8px',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        '& svg': {
            fill: 'white',
        },
    },
    itemImgSvg: {
        width: '35px',
        height: '35px',
        borderRadius: '8px',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        '& svg': {
            fill: 'white',
        },
    },

    listItemTitle: {
        color: '#FFF',
        paddingLeft: '10px',
        display: 'inline-block',
    },
    listItemSubTitle: {
        fontSize: '0.85rem',
        paddingLeft: '10px',
        display: 'block',
    },
    checkIcon: {
        display: 'none',
    },
    bubbleMessage: {
        padding: '5px 10px',
        marginBottom: 5,
        borderRadius: 15,
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        color: '#333',
        '& pre, pre > a': {
            fontFamily: 'inherit',
            fontSize: 'inherit',
            color: '#fff',
            whiteSpace: 'pre-wrap',
            margin: '0',
        },
        '& img': {
            borderRadius: '8px',
        },
        width: 'fit-content',
    },
    bubbleMessageSurvey: {
        width: '100%',
    },
    bubbleLeft: {
        alignItems: 'flex-start',
        float: 'left',
        clear: 'both',
        width: '100%',
    },
    leftBubbleMessage: {
        backgroundColor: '#eaeaec',
    },
    selBubble: {
        padding: '17px 0px',
        maxWidth: 356,
    },
    selfMessage: {
        float: 'right',
        clear: 'both',
    },
    systemMessage: {
        fontSize: '0.8em',
        fontStyle: 'italic',
        backgroundColor: 'white',
        textAlign: 'center',
        width: '100%',
        '& ul, & ol, & li': {
            listStylePosition: 'inside',
        },
    },
    markdownMessage: {
        '& ul, & ol, & li': {
            listStylePosition: 'inside',
        },
    },
    selectedBubbleImg: {
        width: '30px',
        height: '26px',
        display: 'flex',
    },
    timeStyles: {
        float: 'left',
        fontSize: 9,
        padding: '5px 11px',
    },
    surveyImage: {
        width: '35px',
        height: '35px',
        margin: '0px !important',
    },
    surveyIcon: {
        width: '55px',
        height: '55px',
        margin: '0px !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
            width: '35px',
            height: '35px',
            fill: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        },
    },
    bold: {
        fontWeight: 600,
    },
}));

const timeAgoTimeout = 60000;
const ChatMessage = ({
    onSubmitMessage,
    messageObj,
    isRichLink,
    featureFlags,
    lastMessageIndex,
    brand,
    convId,
}) => {
    // states
    const [timeAgo, setTimeAgo] = useState(0);
    // refs
    const timerInterval = useRef();
    const classes = useStyles();

    useEffect(() => {
        // Clear interval and reset timer if message changes
        clearInterval(timerInterval.current);

        // run interval
        if (lastMessageIndex?.responseMessage?.o === messageObj?.responseMessage?.o) {
            timerInterval.current = setInterval(() => {
                setTimeAgo((prevMin) => prevMin + 1);
            }, timeAgoTimeout);
        } else if (lastMessageIndex?.message === messageObj?.message) {
            timerInterval.current = setInterval(() => {
                setTimeAgo((prevMin) => prevMin + 1);
            }, timeAgoTimeout);
        }
        return () => {
            clearInterval(timerInterval.current);
        };
    }, [lastMessageIndex]);

    const {
        bubble,
        bubbleLeft,
        bubbleMessage,
        leftBubbleMessage,
        systemMessage,
        selOptLink,
        selOptLinkSpan,
        selOptLinkTitle,
        selOptLinkSubtitle,
        listItem,
        listItem2,
        itemImg,
        listItemName,
        listItemTitle,
        listItemSubTitle,
        selfMessage,
        timeStyles,
        bold,
        mx2,
        welcome,
        wMessage,
        wMessageTitle,
        logo,
        title,
        checkIcon,
        surveyLink,
        surveyImage,
        surveyIcon,
        markdownMessage,
        bubbleMessageSurvey,
    } = classes;
    /**
     * determines what type of chat to render based on the message
     * that was sent to the websocket
     */
    const determineWhatTypeOfChatToRender = () => {
        if (isServerMessage(messageObj)) {
            if (isReadyMessage(messageObj)) {
                return featureFlags['is-aigo-welcome-message-enabled'] && (
                    <div className={welcome}>
                        <div className={wMessage}>
                            <div className={wMessageTitle}>
                                <ResponsiveImage
                                    alt="logo"
                                    className={logo}
                                    path="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blt4bd54a5d0ac39e2a/5fd285798593bc52457ee33b/flowersIcon.png"
                                    params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                                />
                                <span className={title}>Virtual Assistant</span>
                            </div>
                            <div>
                                <span>Hi there! What can I help you with ?</span>
                            </div>
                        </div>
                    </div>
                );
            }
            if (isRichLink(messageObj, convId)) {
                const richData = isRichLink(messageObj, convId);
                if (featureFlags['bumblebee-web-quick-csat-enabled'] && (richData?.title === 'How did we do?' && richData?.subtitle === 'Click here to take survey'))  {
                    return (
                        <div
                            className={`${bubble} ${bubbleLeft}`}
                            style={{ pointerEvents: richData.clickable === 'N' ? 'none' : 'all' }}
                        >
                            <div className={`${bubbleMessage} ${leftBubbleMessage} ${bubbleMessageSurvey}`}>
                                <ExperienceRating  richData={richData} convId={convId} brandCode={brand.code} />
                            </div>
                        </div>
                    );
                }
                return richData?.title !== 'Click here to pay' && (
                    <>
                        <div
                            className={`${bubble} ${bubbleLeft}`}
                            style={{ pointerEvents: richData.clickable === 'N' ? 'none' : 'all' }}
                            data-testid="rich-link"
                        >
                            <div className={`${bubbleMessage} ${leftBubbleMessage}`}>
                                <a className={selOptLink} href={richData?.url} target="_blank" rel="noreferrer">
                                    <span className={selOptLinkSpan}>
                                        <span className={selOptLinkTitle}>{richData?.title}</span>
                                        <span className={selOptLinkSubtitle}>{getHostname(richData.url)}</span>
                                    </span>
                                    <img
                                        alt=""
                                        src={
                                            `${richData?.assets?.image?.imageType}` === 'url'
                                                ? richData?.assets?.image?.data
                                                : imageMapper?.find((imageObj) => imageObj.imageKey === richData?.assets?.image?.data)?.imageValue
                                        }
                                        className={mx2}
                                        data-testid="image-source"
                                    />
                                </a>
                            </div>
                        </div>
                    </>
                );
            }
            if (isInteractive(messageObj)) {
                let interactiveJsonObject;
                try {
                    interactiveJsonObject = JSON.parse(messageObj?.responseMessage?.o);
                } catch (error) {
                    mbpLogger.logError({
                        appName: process.env.npm_package_name,
                        errorMessage: error,
                        message: `ChatMessage => Error Parsing interactiveData.- convid: ${convId} - ERROR: ${error}`,
                        conversationId: convId,
                    });
                }
                return interactiveJsonObject.interactiveData && (
                    <div>
                        <ChatSelectDialog
                            onSubmitMessage={onSubmitMessage}
                            interactiveData={{ ...interactiveJsonObject?.interactiveData, hover: interactiveJsonObject?.hover }}
                            messageObj={messageObj}
                            messageParsedTitle={ReactHtmlParser(messageObj?.selectedItem?.title)}
                            lastMessageIndex={lastMessageIndex}
                            timeAgo={timeAgo}
                            classList={classes}
                        />
                    </div>
                );
            }
            if (messageObj?.responseMessage?.o) {
                // eslint-disable-next-line no-param-reassign
                const parsedResponseMessage = (urlConverter(messageObj?.responseMessage?.o));
                return typeof messageObj?.responseMessage?.o === 'string' && (
                    <>
                        <div className={`${bubble} ${bubbleLeft}`}>
                            <div
                                className={
                                    `${bubbleMessage}
                            ${leftBubbleMessage}
                            ${messageObj?.responseMessage?.source?.toLowerCase() === 'system' ? systemMessage : ''}
                            `
                                }
                            >
                                <span className={`${markdownMessage}`}>{// eslint-disable-next-line no-nested-ternary
                                    messageObj?.responseMessage?.messageType?.toLowerCase() === 'html'
                                        ? ReactHtmlParser(messageObj?.responseMessage?.o)
                                        : parsedResponseMessage.urlsCount ? ReactHtmlParser(parsedResponseMessage.message)
                                            : <ReactMarkdown source={messageObj?.responseMessage?.o} />
                                }
                                </span>
                            </div>
                            {lastMessageIndex?.responseMessage?.o === messageObj?.responseMessage?.o
                                && (
                                    <span
                                        className={timeStyles}
                                        style={{ marginTop: '-12px' }}
                                        data-testid="time-stamp-text"
                                    > {formatTimeAgo(timeAgo, isSelfMessage(messageObj))}
                                    </span>
                                )}
                        </div>
                    </>
                );
            }
        } if (isSelfMessage(messageObj)) {
            const parsedSelfMessage = (urlConverter(messageObj?.message));

            return typeof messageObj?.message === 'string' ? (
                <div className={`${bubble} ${selfMessage}`}>
                    <div className={bubbleMessage}>
                        <pre>{parsedSelfMessage.urlsCount ? ReactHtmlParser(parsedSelfMessage.message) : messageObj?.message}</pre>
                        {
                            lastMessageIndex?.message === messageObj?.message
                                ? (
                                    <span className={timeStyles} style={{ padding: '2px 0px', float: 'right' }} data-testid="time-stamp-text">
                                        {formatTimeAgo(timeAgo, isSelfMessage(messageObj))}
                                    </span>
                                )
                                : ''
                        }
                    </div>
                </div>
            )
                : (
                    <div className={bubble}>
                        <div className={`${bubbleMessage} ${selfMessage}`}>
                            <span>
                                <div className={`${listItem} ${listItem2}`}>
                                    <ResponsiveImage
                                        alt=""
                                        className={itemImg}
                                        path={messageObj?.message?.selectedImage}
                                        params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                                    />
                                    <span className={listItemName}>
                                        <span className={`${listItemTitle} ${messageObj?.message?.subtitle ? bold : ''}`}>
                                            {messageObj?.message?.title}
                                        </span>
                                        <span className={listItemSubTitle}>
                                            {messageObj?.message?.subtitle}
                                        </span>
                                        <span />
                                    </span>
                                    <span className={checkIcon} />
                                </div>
                            </span>
                        </div>
                    </div>
                );
        }
        if (isSurveyLink(messageObj)) {
            return (
                <>
                    <div className={`${bubble} ${bubbleLeft}`}>
                        <div className={`${bubbleMessage} ${leftBubbleMessage}`}>
                            <a
                                className={`${surveyLink} ${selOptLink}`}
                                href={messageObj.responseMessage?.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <span className={selOptLinkSpan}>
                                    <span className={selOptLinkTitle}>{messageObj.responseMessage?.title}</span>
                                    <span className={selOptLinkSubtitle}>{messageObj.responseMessage?.subtitle}</span>
                                </span>
                                {brand.code === '18F' ? (
                                    <img
                                        alt=""
                                        src="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltb412a4fee6d4f75f/60804e921cac355a10f6c388/tulip-icon.png"
                                        className={surveyImage}
                                    />
                                ) : <span className={`${mx2} ${surveyIcon}`}>{supportListpicker()}</span>}

                            </a>
                        </div>
                    </div>
                </>
            );
        }
        if (messageObj?.responseMessage?.contents?.message && featureFlags['is-csp-chat-widget-enabled']) {
            // eslint-disable-next-line no-param-reassign
            const parsedResponseMessage = (urlConverter(messageObj?.responseMessage?.contents?.message));
            return typeof messageObj?.responseMessage?.contents?.message === 'string' && (
                <>
                    <div className={`${bubble} ${bubbleLeft}`}>
                        <div
                            className={
                                `${bubbleMessage}
                        ${leftBubbleMessage}
                        ${messageObj?.responseMessage?.contents?.from?.source?.toLowerCase() === 'system' ? systemMessage : ''}
                        `
                            }
                        >
                            <span>{// eslint-disable-next-line no-nested-ternary
                                messageObj?.responseMessage?.contents?.messageType?.toLowerCase() === 'html'
                                    ? ReactHtmlParser(messageObj?.responseMessage?.contents?.message)
                                    : parsedResponseMessage.urlsCount ? ReactHtmlParser(parsedResponseMessage.message)
                                        : ReactHtmlParser(messageObj?.responseMessage?.contents?.message)
                            }
                            </span>
                        </div>
                        {lastMessageIndex?.responseMessage?.o === messageObj?.responseMessage?.contents?.message
                            && (
                                <span
                                    className={timeStyles}
                                    style={{ marginTop: '-12px' }}
                                    data-testid="time-stamp-text"
                                > {formatTimeAgo(timeAgo, isSelfMessage(messageObj))}
                                </span>
                            )}
                    </div>
                </>
            );
        }
        if (messageObj?.message?.type === 'richLink') {
            const persistentRichData = messageObj?.message?.richLinkData;
            return persistentRichData?.title !== 'Click here to pay' && (
                <>
                    <div
                        className={`${bubble} ${bubbleLeft}`}
                        style={{ pointerEvents: persistentRichData.clickable === 'N' ? 'none' : 'all' }}
                        data-testid="rich-link"
                    >
                        <div className={`${bubbleMessage} ${leftBubbleMessage}`}>
                            <a className={selOptLink} href={persistentRichData?.url} target="_blank" rel="noreferrer">
                                <span className={selOptLinkSpan}>
                                    <span className={selOptLinkTitle}>{persistentRichData?.title}</span>
                                    <span className={selOptLinkSubtitle}>{getHostname(persistentRichData.url)}</span>
                                </span>
                                <img
                                    alt=""
                                    src={
                                        `${persistentRichData?.assets?.image?.imageType}` === 'url'
                                            ? persistentRichData?.assets?.image?.data
                                            : imageMapper?.find((imageObj) => imageObj.imageKey === persistentRichData?.assets?.image?.data)?.imageValue
                                    }
                                    className={mx2}
                                    data-testid="image-source"
                                />
                            </a>
                        </div>
                    </div>
                </>
            );
        }
        if (messageObj?.sender === 'Customer') {
            return typeof messageObj?.message === 'string'
                && (
                    <div className={`${bubble} ${selfMessage}`}>
                        <div className={bubbleMessage}>
                            <pre>{messageObj?.message}</pre>
                            {
                                lastMessageIndex?.message === messageObj?.message
                                    ? (
                                        <span className={timeStyles} style={{ padding: '2px 0px', float: 'right' }} data-testid="time-stamp-text">
                                            {formatTimeAgo(timeAgo, true)}
                                        </span>
                                    )
                                    : ''
                            }
                        </div>
                    </div>
                );
        }
        if (messageObj?.message?.type === 'interactive') {
            return messageObj.message?.interactiveData && (
                <div>
                    <ChatSelectDialog
                        onSubmitMessage={onSubmitMessage}
                        interactiveData={{ ...messageObj?.message?.interactiveData, hover: messageObj?.message?.hover }}
                        messageObj={messageObj}
                        messageParsedTitle={ReactHtmlParser(messageObj?.selectedItem?.title)}
                        lastMessageIndex={lastMessageIndex}
                        timeAgo={timeAgo}
                        classList={classes}
                    />
                </div>
            );
        }
        if (messageObj?.sender === 'Chatbot' || messageObj?.sender === 'Agent' || messageObj?.sender === 'SYSTEM') {
            const parsedResponseMessage = (urlConverter(messageObj?.message));
            return typeof messageObj?.message === 'string' && (
                <>
                    <div className={`${bubble} ${bubbleLeft}`}>
                        <div
                            className={
                                `${bubbleMessage}
                        ${leftBubbleMessage}
                        ${messageObj?.sender?.toLowerCase() === 'system' ? systemMessage : ''}
                        `
                            }
                        >
                            <span>{
                                // eslint-disable-next-line no-nested-ternary
                                messageObj?.messageType?.toLowerCase() === 'html'
                                    ? ReactHtmlParser(messageObj?.message)
                                    : parsedResponseMessage?.urlsCount ? ReactHtmlParser(parsedResponseMessage.message)
                                        : ReactHtmlParser(messageObj?.message)
                            }
                            </span>
                        </div>
                        {lastMessageIndex?.message === messageObj?.message
                            && (
                                <span
                                    className={timeStyles}
                                    style={{ marginTop: '-12px' }}
                                    data-testid="time-stamp-text"
                                > {formatTimeAgo(timeAgo, false)}
                                </span>
                            )}
                    </div>
                </>
            );
        }
        return null;
    };

    return determineWhatTypeOfChatToRender();
};

ChatMessage.propTypes = {
    messageObj: object.isRequired,
    featureFlags: object.isRequired,
    lastMessageIndex: object,
    brand: object.isRequired,
    // functions
    isRichLink: func,
    onSubmitMessage: func.isRequired,
    convId: string.isRequired,
};

ChatMessage.defaultProps = {
    isRichLink: () => { },
    lastMessageIndex: {},
};
export default ChatMessage;
